import styled, { keyframes } from 'styled-components';
import theme from '../theme';

export const PageWrapper = styled.div`
  font-family: ${theme.typography.fontFamily};
  max-width: 100%;
  /* clip, not hidden: hidden makes this a scroll container and a sticky rail
     inside it would no longer follow the window scroll (see Layout.styles). */
  overflow-x: clip;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.lg};
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
`;

export const PageTitle = styled.h1`
  font-size: ${theme.typography.h1.size};
  font-weight: ${theme.typography.h1.weight};
  color: ${theme.colors.text};
  margin: 0;

  ${theme.media.mobile} {
    font-size: ${theme.typography.h2.size};
  }
`;

export const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  background: ${theme.colors.surface};
  color: ${theme.colors.primary};
  border: 1.5px solid ${theme.colors.primary};
  border-radius: ${theme.radii.sm};
  padding: 8px 20px;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primaryGhost};
  }
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  background: none;
  color: ${theme.colors.primary};
  border: none;
  padding: 0;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  cursor: pointer;
  margin-bottom: ${theme.spacing.lg};
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.primaryDark};
  }
`;

export const Card = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.md};
  border-left: ${({ $accent }) => $accent ? `4px solid ${$accent}` : 'none'};
  overflow: hidden;
  min-width: 0;
  box-sizing: border-box;

  ${theme.media.mobile} {
    padding: ${theme.spacing.md};
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
`;

export const CardTitle = styled.h3`
  font-size: ${theme.typography.h3.size};
  font-weight: ${theme.typography.h3.weight};
  color: ${theme.colors.text};
  margin: 0;
`;

export const CardSubtitle = styled.span`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textMuted};
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};

  ${theme.media.tablet} {
    grid-template-columns: 1fr 1fr;
  }

  ${theme.media.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const InfoItem = styled.div`
  background: ${theme.colors.surfaceHover};
  border-radius: ${theme.radii.sm};
  padding: ${theme.spacing.md};
  min-width: 0;
  overflow: hidden;
`;

export const InfoLabel = styled.div`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing.xs};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const InfoValue = styled.div`
  font-size: ${theme.typography.h3.size};
  font-weight: 600;
  color: ${theme.colors.text};
  overflow-wrap: break-word;
  word-break: break-word;

  ${theme.media.mobile} {
    font-size: ${theme.typography.body.size};
  }
`;

export const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};

  ${theme.media.tablet} {
    grid-template-columns: 1fr;
  }
`;

export const SubCard = styled.div`
  background: ${theme.colors.surfaceHover};
  border-radius: ${theme.radii.sm};
  padding: ${theme.spacing.md};
  border-left: 3px solid ${({ $accent }) => $accent || theme.colors.border};
  min-width: 0;
  overflow: hidden;
`;

export const SubCardTitle = styled.div`
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  color: ${({ $color }) => $color || theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${theme.spacing.sm};
`;

export const Badge = styled.span`
  display: inline-block;
  background: ${theme.colors.primaryGhost};
  color: ${theme.colors.primary};
  font-size: ${theme.typography.tiny.size};
  font-weight: 600;
  padding: 3px 10px;
  border-radius: ${theme.radii.full};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const WebsiteLink = styled.a`
  color: ${theme.colors.primary};
  font-size: ${theme.typography.small.size};
  text-decoration: none;
  overflow-wrap: break-word;
  word-break: break-all;
  &:hover { text-decoration: underline; }
`;

export const DownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.primary};
  font-size: ${theme.typography.small.size};
  font-weight: 500;
  text-decoration: none;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.primaryGhost};
  border-radius: ${theme.radii.sm};
  transition: background ${theme.transitions.fast};

  &:hover {
    background: rgba(7, 132, 167, 0.15);
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.body.size};
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.md};

  ${theme.media.tablet} {
    grid-template-columns: 1fr;
  }
`;

// While the company record is still loading: grey blocks in the shape of the
// page, so the empty state ("No offerings have been added yet") never flashes
// before the data arrives.
const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const PageSkeleton = styled.div.attrs({ 'data-testid': 'page-skeleton', 'aria-busy': 'true', 'aria-label': 'Loading' })`
  display: flex;
  gap: ${theme.spacing.lg};
  align-items: flex-start;

  > div {
    border-radius: ${theme.radii.md};
    background: linear-gradient(90deg, #e9eef3 25%, #f3f6f9 37%, #e9eef3 63%);
    background-size: 400px 100%;
    animation: ${shimmer} 1.2s ease-in-out infinite;
  }

  ${theme.media.tablet} {
    flex-direction: column;
    > div { width: 100% !important; }
  }
`;

// A link-styled button inside running text (e.g. "No competitors yet. Add them").
export const InlineLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: ${theme.colors.primary};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${theme.colors.primaryDark};
  }
`;
